import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = { style: {"padding-left":"1em"} }
const _hoisted_2 = {
  key: 0,
  style: {"font-size":"20pt"}
}
const _hoisted_3 = {
  key: 1,
  style: {"font-size":"20pt"}
}
const _hoisted_4 = {
  key: 2,
  style: {"font-size":"20pt"}
}



// getting strongly typed emit payloads
// https://stackoverflow.com/questions/67094213/how-to-emit-events-with-a-strictly-typed-payload-vue-3-composition-api-type

// we need to import the ValidBatchEntry type in both the setup (to strongly type our emits in the
// emits compiler macro above) and here to type some methods on our component since the compiler
// cannot find the name from the setup method but eslint can. Also, some plugins for VSC (i.e. Vue - Official)
// will continue to throw an error here for the duplicate identifier despite being disabled as
// expected during the build process
// @ts-ignore
// eslint-disable-next-line 
import { parseCsvFile, UploadedRecordType, UploadedBatchEntry, ValidationError, ValidBatchEntry, getFormattedValidationError } from '@/Services/Helper/bulk-csv-parser';
import { defineComponent } from 'vue';
import { MasterLookupWrapper } from '@/Models/Lookup/MasterLookupWrapper';
import { LookupKeys } from '@/Enums/LookupKeys';
import { LookupService } from '@/Services/lookup-service';
import { IProvider } from '@/Models/IProvider';
import { ProviderService } from '@/Services/provider-service';
import DataCorrectionModal from './DataCorrectionModal.vue';
import SingleErrorMessage from '../Participant/Common/SingleErrorMessage.vue';
import { IDataTableHeader } from '@/Models/IDataTableHeader';

// File -> ParsingResult -> UploadedBatchEntry -> ValidBatchEntry
// Now all of the individual components for editing will take an uploaded batch entry object
// and dispatch to the right sub component based on the UploadedRecordType associated with
// the parameterized object.
// Then we have the workflow for manually changing the UploadedBatchEntry that isn't valid
// into the state that can be mapped into a ValidBatchEntry, but that's done by review and
// manual data updates on the primary display table

const uploadedDrugTestDisplayHeaders = [
	{title: "Participant #", key: "id", sortable: true},
	{title: "First Name", key: "firstName", sortable: false}, 
	{title: "Last Name", key: "lastName", sortable: false},
	{title: "Type", key: "drugTestTypeIds", sortable: false},
	{title: "Test Date", key: "datePerformed", sortable: false},
	{title: "Location", key: "drugTestLocationId", sortable: false},
	{title: "Session Type", key: "drugTestSessionTypeId", sortable: false},
	{title: "Method", key: "drugTestMethodId", sortable: false},
	{title: "Test Performed By", key: "performedBy", sortable: false}, 
	{title: "Drug Test Result", key: "drugTestResultId", sortable: false},
	{title: "Observed", key: "drugTestObservationId", sortable: false},
	{title: "Valid", key: "isValid", sortable: true, sort(a: boolean, b: boolean) {
		if (a && !b) {
			return -1;
		} else if (b && !a) {
			return 1;
		}
		return 0
	},},
	{title: "Edit", key: "actionEdit", sortable: false},
];

const uploadedTreatmentDisplayHeaders = [
	{title: "Participant #", key: "id", sortable: true},
	{title: "First Name", key: "firstName", sortable: false},
	{title: "Last Name", key: "lastName", sortable: false},
	{title: "Type", key: "typeId", sortable: false},
	{title: "Start Date", key: "startDate", sortable: false},
	{title: "End Date", key: "endDate", sortable: false},
	{title: "Status", key: "statusId", sortable: false},
	{title: "Provider", key: "providerId", sortable: false},
	{title: "Minutes", key: "minutes", sortable: false},
	{title: "Valid", key: "isValid", sortable: true, sort(a: boolean, b: boolean) {
		if (a && !b) {
			return -1;
		} else if (b && !a) {
			return 1;
		}
		return 0
	},},
	{title: "Edit", key: "actionEdit", sortable: false},
]

const __default__ = defineComponent({
	name: "batch-upload-modal",
	// emits defined in setup outside of composition api
	props: {
		showDialog: {
			type: Boolean,
			required: true,
		}
	},
	components: {
		DataCorrectionModal,
		SingleErrorMessage,
	},
	created() {
		this.isLoading = true;

		const requiredLookups = [
			LookupKeys.DrugTestMethod,
			LookupKeys.DrugTestLocation,
			LookupKeys.DrugTestSessionType,
			LookupKeys.DrugTestType,
			LookupKeys.DrugTestResult,
			LookupKeys.DrugTestObservationType,
			LookupKeys.TreatmentStatus,
			LookupKeys.TreatmentType,
		];

		return Promise.all([
			LookupService.getLookupsByKey(requiredLookups),
			ProviderService.getActiveList(),
		]).then(([
			lookups,
			providers,
		]: [MasterLookupWrapper, IProvider[]]) => {
			this.lookups = lookups;
			this.providers = providers;
		}).catch((err) => {
			this.catchError(err);
		}).finally(() => {
			this.isLoading = false;
		});
	},
	data: () => ({
		visible: false,
		showCorrectionEditor: false,
		isProcessing: false,
		isLoading: false,


		uploadTypeOptions: [
			{id: 1, description: "Drug Test", headers: uploadedDrugTestDisplayHeaders},
			{id: 2, description: "Treatment", headers: uploadedTreatmentDisplayHeaders},
		] as {id: number, description: string, headers: IDataTableHeader[]}[], // no satisfies in TS 4.5
		file: null as File | null,
		parsedData: [] as UploadedBatchEntry[],
		lookups: null as MasterLookupWrapper | null,
		providers: [] as IProvider[],
		sortBy: [],


		fileSelection: null as File | null,
		fileUploadType: null as number | null,
		selectedInvalidParsedData: null as UploadedBatchEntry | null,


		error: "",
	}),
	methods: {		
		catchError(err: Error) {
			try {
				this.error = err.message;	
			} catch (errError: unknown) {
				this.error = "An unexpected error has occured. Please try the operation again.";
			}
		},
		handleNestedModalClosure() {
			this.showCorrectionEditor = false;
			this.selectedInvalidParsedData = null;
		},
		getErrorTooltipForRow(errors: ValidationError[]): string {
			if (errors.length == 0) {
				return "";
			}
			return errors.map(getFormattedValidationError).join(";\n");
		},
		/**
		 * Used in rendering the rows which are based on lookup values, this method will return the lookup
		 * description for a given id and lookup key as a string.
		 * 
		 * @param {number} id the id value of the item being looked up, this comes from the csv column
		 * being parsed as an id number
		 * @param {UploadedRecordType} type the type of record being processed, this determines which
		 * of the lists will be read from in the lookupKeys parameter
		 * @param {(number | null)[]} lookupKeys a list of lookupKeys associated with each potential
		 * UploadedRecordType. This allows for distinct lookups for overlapping keyIds between different
		 * record types. Assumes at least one of the values is non null but defaults to providing an empty
		 * string and an error if they all are null
		 */
		getTypedLookupValue(id: number, type: UploadedRecordType, lookupKeys: (number | null)[]): string {
			// here we assume that the UploadedRecordTypes are in the same order as the lookup keys
			if (type == "drug test") {
				return this.lookups?.lookupLists[lookupKeys[0] ?? -1].find(ll => ll.id == id)?.description ?? ""; 
			} else if (type == "treatment") {
				return this.lookups?.lookupLists[lookupKeys[1] ?? -1].find(ll => ll.id == id)?.description ?? ""; 
			} else {
				this.catchError(new Error("encountered unexpected default in typed lookup"))
				return "";
			}
		},
		// onClose is the listener for close events of child components. See onCloseDialog for the close handler
		// for this component itself
		onClose(): void {
			this.handleNestedModalClosure();
		},
		// onCloseDialog is the close handler for this component itself. See onClose for the listener for close
		// events of child components
		onCloseDialog(): void {
			if (confirm("Are you sure you would like to close? This will not save any data. All changes will be lost.")) {
				this.handleNestedModalClosure();
				this.$emit("close");
			}
		},
		onEdit(data: UploadedBatchEntry): void {
			this.selectedInvalidParsedData = data;
			this.showCorrectionEditor = true;
		},
		onUpdate(): void {
			// since objects are updated in place, we don't need to catch the emitted, updated value and replace it in line
			// we don't do it for clarity due to the potential for very large tables and not wanting to traverse 10,000 elements
			// just to find which record to update
			this.handleNestedModalClosure();
		},
		onSubmit(): void {
			if (this.hasInvalidRows && !confirm('Data has unresovled validation errors. Are you sure you want to submit the valid data? Any rows with invalid data will not be processed.')) {
				return;
			}
			this.$emit("prepared-data-upload", this.validData);
		},
		onProcessFile(): void {
			this.parsedData = [];
			this.isProcessing = true;

			parseCsvFile(this.file!).then((parsedData: UploadedBatchEntry[]) => {
					this.parsedData = parsedData;
				}).catch((err) => {
					this.catchError(err)
				}).finally(() => {
					this.isProcessing = false;
				});
		},
	},
	computed: {
		hasInvalidRows(): boolean {
			return this.invalidRows.length > 0;
		},
		invalidRows(): UploadedBatchEntry[] {
			return this.parsedData.filter(d => d.validationErrors.length != 0);
		},
		submitButtonText(): string {
			return this.hasInvalidRows ? "Submit Valid Rows" : "Submit"
		},
		tableHeaders(): IDataTableHeader[] {
			if (!this.fileUploadType) {
				return [];
			}
			return this.uploadTypeOptions.find(uto => uto.id == this.fileUploadType)!.headers; 
		},
		validData(): ValidBatchEntry[] {
			return this.parsedData.filter(d => d.validationErrors.length == 0).map(d => ({
				...d,
				validationErrors: null,
			}));
		},
		validRows(): UploadedBatchEntry[] {
			return this.parsedData.filter(d => d.validationErrors.length == 0);
		},
		validationErrors(): ValidationError[] {
			return this.parsedData.filter(d => d.validationErrors.length != 0).reduce((acc, curr) => {
				return acc.concat(curr.validationErrors);
			}, [] as ValidationError[])
		},
		validationErrorPreviews(): string[] {
			if (this.validationErrors.length == 0) {
				return ['None'];
			} else if (this.validationErrors.length <= 10) {
				return this.validationErrors.map(getFormattedValidationError);
			} else {
				let errorPreviews = [] as string[];
				errorPreviews = errorPreviews.concat(this.validationErrors.slice(0,9).map(getFormattedValidationError));
				errorPreviews.push(`and ${this.validationErrors.length - 9} more errors.`);
				return errorPreviews;
			}
		}
	},
	watch: {
		fileSelection(newVal: File): void {
			this.error = "";
			
			//fileSelection is now a single file (not a file array)
			//Below is deprecated:
			// if (newVal.length > 1) {
			// 	this.catchError(new Error(`Only one file may be uploaded at a time. got=${newVal.length}`));
			// 	return;
			// }
	
			const file = newVal;
			if (!file) {
				this.file = file;
				return;
			}
			
			if (file.type != 'text/csv') {
				this.catchError(new Error(`Invalid file type uploaded:${file.type}`));
				return;
			}

			const MB = 1024 * 1024 // 1 MB
			if (file.size > (2 * MB)) { 
				this.catchError(new Error(`File too large. ${Math.round(file.size / MB * 100) / 100}MB/2MB`));
				return;
			}
			this.file = file;
		},
		showDialog(newVal: boolean, oldVal: boolean): void {
			// going from visible to hidden
			if (oldVal && !newVal) {
				this.handleNestedModalClosure();
				this.parsedData = [];
				this.fileSelection = null;
				this.fileUploadType = null;
			}
			this.visible = newVal;
		}
	}
})


export default /*@__PURE__*/_defineComponent({
  ...__default__,
  emits: ["close", "prepared-data-upload"],
  setup(__props) {

// @ts-ignore
// eslint-disable-next-line


return (_ctx: any,_cache: any) => {
  const _component_v_toolbar = _resolveComponent("v-toolbar")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_file_input = _resolveComponent("v-file-input")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_progress_linear = _resolveComponent("v-progress-linear")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_data_table = _resolveComponent("v-data-table")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createElementBlock("div", null, [
    (_ctx.selectedInvalidParsedData != null && _ctx.lookups != null)
      ? (_openBlock(), _createBlock(DataCorrectionModal, {
          key: 0,
          providedParsedData: _ctx.selectedInvalidParsedData,
          showDialog: _ctx.showCorrectionEditor,
          lookups: _ctx.lookups,
          providers: _ctx.providers,
          onClose: _ctx.onClose,
          onUpdate: _ctx.onUpdate
        }, null, 8, ["providedParsedData", "showDialog", "lookups", "providers", "onClose", "onUpdate"]))
      : _createCommentVNode("", true),
    _createVNode(_component_v_dialog, {
      modelValue: _ctx.visible,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_ctx.visible) = $event)),
      "onClick:outside": _ctx.onCloseDialog,
      transition: "dialog-top-transition",
      width: "90%",
      scrollable: "",
      "scroll-strategy": "none"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_toolbar, { title: "Bulk Data Upload" }),
            _createVNode(_component_v_spacer),
            _createVNode(_component_v_card_text, { class: "mt-6" }, {
              default: _withCtx(() => [
                (_ctx.error != '')
                  ? (_openBlock(), _createBlock(_component_v_row, { key: 0 }, {
                      default: _withCtx(() => [
                        _createVNode(SingleErrorMessage, { errorMessage: _ctx.error }, null, 8, ["errorMessage"])
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_component_v_row, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_col, {
                      cols: "12",
                      md: "3"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_select, {
                          modelValue: _ctx.fileUploadType,
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_ctx.fileUploadType) = $event)),
                          items: _ctx.uploadTypeOptions,
                          "item-value": "id",
                          "item-title": "description"
                        }, null, 8, ["modelValue", "items"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, { md: "6" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_file_input, {
                          modelValue: _ctx.fileSelection,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_ctx.fileSelection) = $event)),
                          accept: "text/csv",
                          label: "Select Document for Processing"
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, { md: "3" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_btn, {
                          color: "primary",
                          onClick: _ctx.onProcessFile,
                          disabled: _ctx.file == null
                        }, {
                          default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                            _createTextVNode("Process File", -1)
                          ]))]),
                          _: 1
                        }, 8, ["onClick", "disabled"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                (_ctx.file != null && _ctx.isProcessing)
                  ? (_openBlock(), _createBlock(_component_v_row, { key: 1 }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_progress_linear, {
                          indeterminate: "",
                          color: "primary",
                          height: "10",
                          rounded: ""
                        })
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                (_ctx.file != null && (_ctx.file.size / 1024 > 500))
                  ? (_openBlock(), _createBlock(_component_v_row, { key: 2 }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_spacer),
                        _createVNode(_component_v_col, {
                          cols: "12",
                          md: "6"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card, {
                              color: "warning",
                              "prepend-icon": "mdi-message-alert-outline",
                              title: "Alert",
                              text: "Processing files over 500kb may lead to extended response times. To improve application responsiveness, split larger files into separate pieces and process them sequentially."
                            })
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_spacer)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_component_v_row, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_col, {
                      cols: "12",
                      md: "12",
                      align: "center"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card, null, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card_text, null, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_row, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_col, {
                                      cols: "12",
                                      md: "12",
                                      align: "left"
                                    }, {
                                      default: _withCtx(() => [
                                        (_ctx.hasInvalidRows)
                                          ? (_openBlock(), _createBlock(_component_v_card, {
                                              key: 0,
                                              color: "error",
                                              "prepend-icon": "mdi-alert",
                                              title: "Errors"
                                            }, {
                                              default: _withCtx(() => [
                                                _createVNode(_component_v_card_text, null, {
                                                  default: _withCtx(() => [
                                                    _createElementVNode("ul", _hoisted_1, [
                                                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.validationErrorPreviews, (err) => {
                                                        return (_openBlock(), _createElementBlock("li", {
                                                          key: _ctx.validationErrorPreviews.indexOf(err),
                                                          class: "mt-3"
                                                        }, _toDisplayString(err), 1))
                                                      }), 128))
                                                    ])
                                                  ]),
                                                  _: 1
                                                })
                                              ]),
                                              _: 1
                                            }))
                                          : _createCommentVNode("", true),
                                        (!_ctx.hasInvalidRows && _ctx.parsedData.length > 0)
                                          ? (_openBlock(), _createBlock(_component_v_card, {
                                              key: 1,
                                              color: "green",
                                              "prepend-icon": "mdi-check-all",
                                              title: "No Errors",
                                              text: "All data type errors have been resolved."
                                            }))
                                          : _createCommentVNode("", true)
                                      ]),
                                      _: 1
                                    })
                                  ]),
                                  _: 1
                                }),
                                _createVNode(_component_v_row, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_col, {
                                      cols: "12",
                                      md: "12",
                                      align: "center"
                                    }, {
                                      default: _withCtx(() => [
                                        (_ctx.fileUploadType == null)
                                          ? (_openBlock(), _createElementBlock("span", _hoisted_2, "Please select a file upload type."))
                                          : (_ctx.fileUploadType != null && _ctx.file == null)
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_3, "Please upload a CSV file for processing."))
                                            : (_ctx.fileUploadType != null && _ctx.isProcessing)
                                              ? (_openBlock(), _createElementBlock("span", _hoisted_4, "Processing CSV data..."))
                                              : (_openBlock(), _createBlock(_component_v_data_table, {
                                                  key: 3,
                                                  headers: _ctx.tableHeaders,
                                                  items: _ctx.parsedData,
                                                  "sort-by": _ctx.sortBy,
                                                  "item-value": "id",
                                                  noDataText: "Upload a file to begin.",
                                                  style: {"width":"'max-content'"}
                                                }, {
                                                  [`item.firstName`]: _withCtx(({ item }) => [
                                                    _createElementVNode("span", null, _toDisplayString(item.data.participants[0].firstName), 1)
                                                  ]),
                                                  [`item.lastName`]: _withCtx(({ item }) => [
                                                    _createElementVNode("span", null, _toDisplayString(item.data.participants[0].lastName), 1)
                                                  ]),
                                                  [`item.typeId`]: _withCtx(({ item }) => [
                                                    _createElementVNode("span", null, _toDisplayString(_ctx.getTypedLookupValue(item.data.typeId, "treatment", [null, _unref(LookupKeys).TreatmentType])), 1)
                                                  ]),
                                                  [`item.drugTestTypeIds`]: _withCtx(({ item }) => [
                                                    _createElementVNode("span", null, _toDisplayString(item.data.drugTestTypeIds ? item.data.drugTestTypeIds.map((id) => {
																return _ctx.getTypedLookupValue(id, "drug test", [_unref(LookupKeys).DrugTestType, null])
															}).join("; ")
														: ""), 1)
                                                  ]),
                                                  [`item.datePerformed`]: _withCtx(({ item }) => [
                                                    _createElementVNode("span", null, _toDisplayString(item.data.datePerformed), 1)
                                                  ]),
                                                  [`item.drugTestLocationId`]: _withCtx(({ item }) => [
                                                    _createElementVNode("span", null, _toDisplayString(_ctx.getTypedLookupValue(item.data.drugTestLocationId, "drug test", [_unref(LookupKeys).DrugTestLocation, null])), 1)
                                                  ]),
                                                  [`item.drugTestSessionTypeId`]: _withCtx(({ item }) => [
                                                    _createElementVNode("span", null, _toDisplayString(_ctx.getTypedLookupValue(item.data.drugTestSessionTypeId, "drug test", [_unref(LookupKeys).DrugTestSessionType, null])), 1)
                                                  ]),
                                                  [`item.drugTestMethodId`]: _withCtx(({ item }) => [
                                                    _createElementVNode("span", null, _toDisplayString(_ctx.getTypedLookupValue(item.data.drugTestMethodId, "drug test", [_unref(LookupKeys).DrugTestMethod, null])), 1)
                                                  ]),
                                                  [`item.performedBy`]: _withCtx(({ item }) => [
                                                    _createElementVNode("span", null, _toDisplayString(item.data.performedBy), 1)
                                                  ]),
                                                  [`item.drugTestResultId`]: _withCtx(({ item }) => [
                                                    _createElementVNode("span", null, _toDisplayString(_ctx.getTypedLookupValue(item.data.participants[0]?.drugTestResultId, "drug test", [_unref(LookupKeys).DrugTestResult, null])), 1)
                                                  ]),
                                                  [`item.drugTestObservationId`]: _withCtx(({ item }) => [
                                                    _createElementVNode("span", null, _toDisplayString(_ctx.getTypedLookupValue(item.data.drugTestObservationId, "drug test", [_unref(LookupKeys).DrugTestObservationType, null])), 1)
                                                  ]),
                                                  [`item.startDate`]: _withCtx(({ item }) => [
                                                    _createElementVNode("span", null, _toDisplayString(item.data.startDate), 1)
                                                  ]),
                                                  [`item.endDate`]: _withCtx(({ item }) => [
                                                    _createElementVNode("span", null, _toDisplayString(item.data.endDate), 1)
                                                  ]),
                                                  [`item.statusId`]: _withCtx(({ item }) => [
                                                    _createElementVNode("span", null, _toDisplayString(_ctx.getTypedLookupValue(item.data.statusId, "treatment", [null, _unref(LookupKeys).TreatmentStatus])), 1)
                                                  ]),
                                                  [`item.providerId`]: _withCtx(({ item }) => [
                                                    _createElementVNode("span", null, _toDisplayString(_ctx.providers.find(p => p.id == item.data.providerId)?.name ?? "Unknown"), 1)
                                                  ]),
                                                  [`item.minutes`]: _withCtx(({ item }) => [
                                                    _createElementVNode("span", null, _toDisplayString(item.data.minutes), 1)
                                                  ]),
                                                  [`item.isValid`]: _withCtx(({ item }) => [
                                                    (_ctx.getErrorTooltipForRow(item.validationErrors) != '')
                                                      ? (_openBlock(), _createBlock(_component_v_tooltip, {
                                                          key: 0,
                                                          text: _ctx.getErrorTooltipForRow(item.validationErrors),
                                                          style: {"white-space":"pre-line"},
                                                          location: "bottom"
                                                        }, {
                                                          activator: _withCtx(({ props }) => [
                                                            _createVNode(_component_v_icon, _mergeProps(props, {
                                                              icon: "mdi-alert",
                                                              color: "red"
                                                            }), null, 16)
                                                          ]),
                                                          _: 1
                                                        }, 8, ["text"]))
                                                      : (_openBlock(), _createBlock(_component_v_tooltip, {
                                                          key: 1,
                                                          text: "No validation errors for row",
                                                          location: "bottom"
                                                        }, {
                                                          activator: _withCtx(({ props }) => [
                                                            _createVNode(_component_v_icon, _mergeProps(props, {
                                                              icon: "mdi-check-bold",
                                                              color: "green"
                                                            }), null, 16)
                                                          ]),
                                                          _: 1
                                                        }))
                                                  ]),
                                                  [`item.actionEdit`]: _withCtx(({ item }) => [
                                                    (item.validationErrors.length != 0)
                                                      ? (_openBlock(), _createBlock(_component_v_btn, {
                                                          key: 0,
                                                          onClick: ($event: any) => (_ctx.onEdit(item))
                                                        }, {
                                                          default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                                                            _createTextVNode("Fix Row Data", -1)
                                                          ]))]),
                                                          _: 1
                                                        }, 8, ["onClick"]))
                                                      : _createCommentVNode("", true)
                                                  ]),
                                                  _: 2
                                                }, 1032, ["headers", "items", "sort-by"]))
                                      ]),
                                      _: 1
                                    })
                                  ]),
                                  _: 1
                                }),
                                (_ctx.parsedData.length > 0)
                                  ? (_openBlock(), _createBlock(_component_v_row, { key: 0 }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_spacer),
                                        _createVNode(_component_v_col, {
                                          cols: "12",
                                          md: "4"
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_btn, { onClick: _ctx.onSubmit }, {
                                              default: _withCtx(() => [
                                                _createTextVNode(_toDisplayString(_ctx.submitButtonText), 1)
                                              ]),
                                              _: 1
                                            }, 8, ["onClick"])
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }))
                                  : _createCommentVNode("", true)
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue", "onClick:outside"])
  ]))
}
}

})